import _ from 'lodash';

import {
  firebaseAddDoc,
  firebaseDeleteDoc,
  firebaseGetById,
  firebaseGetAll,
  firebaseGetBy,
  firebaseUpdateDoc,
  firebaseGetDocRef,
} from '@client/api/firebase.api';
import {
  cleanData,
  createMetadata,
  getAuthUser,
  prepareMetadataUpdate,
  updateMetadata,
} from '@client/services/common.service';
import { getFeatureRefs } from '@client/services/feature.service';
import { isNewMode } from '@client/utils/form.util';
import { logger } from '@client/utils/logger.util';
import {
  ESort,
  EStatus,
  type ISubscription,
  type IFeature,
  CSubscriptionsPath,
  CFeaturesPath,
} from '@common/types';

import type { TPaginateProps } from '@client/types/antd.type';
import type { TablePaginationConfig } from 'antd/es/table/interface';
import type { User } from 'firebase/auth';
import type { DocumentData } from 'firebase/firestore';

export const createSubscription = async (
  data: ISubscription,
  features: IFeature[] = [],
) => {
  try {
    const user = getAuthUser();

    const featureRefs = await getFeatureRefs(features);

    return firebaseAddDoc<ISubscription>({
      collectionPath: CSubscriptionsPath,
      data: {
        ...cleanData(data),
        assignedFeatures: featureRefs,
        status: EStatus.PROCESSING,
        metadata: createMetadata(user),
      },
    });
  } catch (error) {
    logger.error('createSubscription', error);
    throw error;
  }
};

export const getSubscription = async (
  id: Required<ISubscription['id']>,
): Promise<ISubscription | null> => {
  try {
    if (isNewMode(id)) return null;
    const subscription = await firebaseGetById<ISubscription>({
      collectionPath: CSubscriptionsPath,
      document: id as string,
    });
    return subscription as ISubscription;
  } catch (error) {
    logger.error('getSubscription', error);
    throw error;
  }
};

export const updateSubscription = async (
  id: Required<ISubscription['id']>,
  data: ISubscription,
  features: IFeature[] = [],
) => {
  try {
    const user = getAuthUser();

    const existing = await firebaseGetById<ISubscription>({
      collectionPath: CSubscriptionsPath,
      document: id,
    });

    let existingMetadata = {};
    if (existing) {
      existingMetadata = existing.metadata ?? {};
    }

    const featureRefs = await getFeatureRefs(features);

    return firebaseUpdateDoc<ISubscription>({
      collectionPath: CSubscriptionsPath,
      document: id as string,
      data: {
        ...cleanData(data),
        assignedFeatures: featureRefs,
        metadata: {
          ...existingMetadata,
          ...updateMetadata(user as User),
        },
      },
    });
  } catch (error) {
    logger.error('updateSubscription', error);
    throw error;
  }
};

export const deleteSubscription = async (id: Required<ISubscription['id']>) =>
  firebaseDeleteDoc({
    collectionPath: CSubscriptionsPath,
    document: id as string,
  });

export const deactivateSubscription = async (
  id: Required<ISubscription['id']>,
) => {
  await firebaseUpdateDoc({
    collectionPath: CSubscriptionsPath,
    document: id as string,
    data: {
      status: EStatus.DEACTIVATED,
    },
  });

  return getUserSubscriptions();
};

export const toggleSubscriptionStatus = async (subscription: ISubscription) =>
  firebaseUpdateDoc<ISubscription>({
    collectionPath: CSubscriptionsPath,
    document: subscription.id as string,
    data: {
      status:
        subscription.status === EStatus.ACTIVE
          ? EStatus.DEACTIVATED
          : EStatus.ACTIVE,
      ...prepareMetadataUpdate<ISubscription>(subscription),
    },
  });

export const getAllSubscriptions = async (
  params?: TPaginateProps & TablePaginationConfig,
) => {
  logger.debug('params', params);

  try {
    const subscriptions = await firebaseGetAll<ISubscription & { id: string }>({
      collectionPath: CSubscriptionsPath,
    });

    const data: ISubscription[] = [];
    if ('length' in subscriptions) {
      subscriptions?.forEach((doc: ISubscription & { id: string }) => {
        data.push(_.merge(doc, { id: doc.id }) as ISubscription);
      });
    }

    return data;
  } catch (error) {
    logger.error('getSubscriptions', error);
    throw error;
  }
};

export const getUserSubscriptions = async (
  params?: TPaginateProps & TablePaginationConfig,
) => {
  const user = getAuthUser();

  try {
    const result =
      (await firebaseGetBy({
        collectionPath: CSubscriptionsPath,
        wheres: [{ field: 'metadata.createdBy.uid', value: user.uid }],
        optional: {
          order: 'metadata.updatedAt',
          orderDirection: ESort.DESC,
          limit: params?.pageSize,
        },
      })) || [];

    return {
      [CSubscriptionsPath]: result.map(
        (doc: DocumentData) => _.merge({ id: doc.id }, doc) as ISubscription,
      ),
      total: result.length,
    };
  } catch (error) {
    logger.error('getSubscriptions error', error);
    throw error;
  }
};

export const getActiveSubscriptions = async (
  params?: TPaginateProps & TablePaginationConfig,
) => {
  const user = getAuthUser();

  try {
    // Query for active subscriptions
    const activeSubscriptions = await firebaseGetBy({
      collectionPath: CSubscriptionsPath,
      wheres: [
        { field: 'status', value: EStatus.ACTIVE },
        { field: 'metadata.createdBy.uid', value: user.uid },
      ],
      optional: {
        order: 'metadata.updatedAt',
        orderDirection: ESort.DESC,
        limit: params?.pageSize,
      },
    });

    return {
      [CSubscriptionsPath]: activeSubscriptions.map(
        (doc: DocumentData) => _.merge({ id: doc.id }, doc) as ISubscription,
      ),
      total: activeSubscriptions.length,
    };
  } catch (error) {
    logger.error('getActiveSubscriptions error', error);
    throw error;
  }
};

export const getSubscriptionsByFeature = async (featureId: string) => {
  const user = getAuthUser();

  try {
    const featureRef = firebaseGetDocRef({
      collectionPath: CFeaturesPath,
      document: featureId,
    });

    const result = await firebaseGetBy<ISubscription>({
      collectionPath: CSubscriptionsPath,
      wheres: [
        { field: 'metadata.createdBy.uid', value: user.uid },
        {
          field: 'assignedFeatures',
          operator: 'array-contains',
          value: featureRef,
        },
      ],
    });

    return result.map((doc) => _.merge({ id: doc.id }, doc) as ISubscription);
  } catch (error) {
    logger.error('getSubscriptionsByFeature error', error);
    throw error;
  }
};

export const syncFeatureSubscriptions = async ({
  featureId,
  subscriptions,
}: {
  featureId: string;
  subscriptions: ISubscription[];
}) => {
  try {
    const existingSubscriptions = await getSubscriptionsByFeature(featureId);
    const existingIds = existingSubscriptions.map((s) => s.id);
    const currentIds = subscriptions.map((s) => s.id);

    const toAdd = subscriptions.filter((s) => !existingIds.includes(s.id));
    const toRemove = existingSubscriptions.filter(
      (s) => !currentIds.includes(s.id),
    );

    const featureRef = firebaseGetDocRef({
      collectionPath: CFeaturesPath,
      document: featureId,
    });

    const user = getAuthUser();

    // Process removals
    for (const sub of toRemove) {
      const existing = await firebaseGetById<ISubscription>({
        collectionPath: CSubscriptionsPath,
        document: sub.id as string,
      });
      const assignedFeatures = (existing.assignedFeatures || []).filter(
        (ref: any) => ref.id !== featureId,
      );
      await firebaseUpdateDoc<ISubscription>({
        collectionPath: CSubscriptionsPath,
        document: sub.id as string,
        data: {
          assignedFeatures,
          metadata: {
            ...(existing.metadata || {}),
            ...updateMetadata(user),
          },
        },
      });
    }

    // Process additions
    for (const sub of toAdd) {
      const existing = await firebaseGetById<ISubscription>({
        collectionPath: CSubscriptionsPath,
        document: sub.id as string,
      });
      const assignedFeatures = [...(existing.assignedFeatures || [])];
      if (!assignedFeatures.some((ref: any) => ref.id === featureId)) {
        assignedFeatures.push(featureRef);
      }
      await firebaseUpdateDoc<ISubscription>({
        collectionPath: CSubscriptionsPath,
        document: sub.id as string,
        data: {
          assignedFeatures,
          metadata: {
            ...(existing.metadata || {}),
            ...updateMetadata(user),
          },
        },
      });
    }
  } catch (error) {
    logger.error('syncFeatureSubscriptions error', error);
    throw error;
  }
};
